import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Quote, Star, ArrowRight, Heart, MapPin, Play, X,
  Filter, Sparkles, Users, Tent, Mountain, Building,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import PageStickyNav from "@/components/PageStickyNav";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const HERO = "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=2400&q=80";

// 16 testimonials with varied tones & properties.
const TESTIMONIALS = [
  {
    id: 1,
    quote: {
      en: "Three hours from Marrakech and we slept under more stars than I knew existed. The team had warmed our bed with hot bottles before we even arrived at the bivouac.",
      fr: "À trois heures de Marrakech, nous avons dormi sous plus d'étoiles que je n'en avais jamais vues. L'équipe avait réchauffé notre lit avec des bouillottes avant même notre arrivée au bivouac.",
      es: "A tres horas de Marrakech, dormimos bajo más estrellas de las que conocía. El equipo había calentado nuestra cama con bolsas de agua antes incluso de llegar al bivac.",
    },
    name: "Émilie & Marc",
    origin: "Paris, France",
    rating: 5,
    property: "bivouacs-xaluca",
    propertyName: "Bivouacs Xaluca",
    type: "bivouac",
    audience: "couple",
    date: { en: "October 2025", fr: "Octobre 2025", es: "Octubre 2025" },
    accent: "#C16542",
  },
  {
    id: 2,
    quote: {
      en: "Our wedding in the dunes was everything we dreamed of. They turned a kasbah into a chapel, a dune into a dance floor — and remembered my mother's gluten-free diet.",
      fr: "Notre mariage dans les dunes a tout dépassé. Ils ont transformé une kasbah en chapelle, une dune en piste de danse — et se sont souvenus du régime sans gluten de ma mère.",
      es: "Nuestra boda en las dunas superó todo lo soñado. Convirtieron una kasbah en capilla, una duna en pista de baile — y recordaron la dieta sin gluten de mi madre.",
    },
    name: "Sofia & James",
    origin: "London, UK",
    rating: 5,
    property: "kasbah-hotel-xaluca-arfoud",
    propertyName: "Kasbah Hotel Xaluca",
    type: "hotel",
    audience: "wedding",
    date: { en: "May 2025", fr: "Mai 2025", es: "Mayo 2025" },
    image: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80",
    accent: "#D4A373",
  },
  {
    id: 3,
    quote: {
      en: "I came to Lake Sidi Ali to write — and ended up not writing. The cedar woods do something to your hours that I will be chasing for the rest of my life.",
      fr: "Je suis venue au lac Sidi Ali pour écrire — et je n'ai rien écrit. Les forêts de cèdres font quelque chose au temps que je chercherai à retrouver toute ma vie.",
      es: "Vine al lago Sidi Ali a escribir — y no escribí. Los cedros hacen algo al tiempo que perseguiré el resto de mi vida.",
    },
    name: "Ana Ruiz",
    origin: "Madrid, España",
    rating: 5,
    property: "hotel-xaluca-spa-aguelmame-sidi-ali",
    propertyName: "Hotel Xaluca Sidi Ali",
    type: "hotel",
    audience: "solo",
    date: { en: "March 2025", fr: "Mars 2025", es: "Marzo 2025" },
    accent: "#5C7C4F",
  },
  {
    id: 4,
    quote: {
      en: "Our kids — 6 and 9 — still ask about Tayeb's stories around the fire. We came for a holiday and went home with a Moroccan family.",
      fr: "Nos enfants — 6 et 9 ans — parlent encore des histoires de Tayeb autour du feu. Nous sommes venus en vacances et repartis avec une famille marocaine.",
      es: "Nuestros hijos — 6 y 9 años — siguen recordando las historias de Tayeb alrededor del fuego. Vinimos de vacaciones y volvimos con una familia marroquí.",
    },
    name: "The Hartmann family",
    origin: "Munich, Germany",
    rating: 5,
    property: "kasbah-hotel-tombouctou",
    propertyName: "Kasbah Hotel Tombouctou",
    type: "hotel",
    audience: "family",
    date: { en: "April 2025", fr: "Avril 2025", es: "Abril 2025" },
    accent: "#2C2621",
  },
  {
    id: 5,
    quote: {
      en: "The 4x4 day across the hammada to the Todgha gorges was the single most spectacular drive of my life. Our driver, Mohamed, is a poet behind the wheel.",
      fr: "La journée en 4x4 à travers la hammada jusqu'aux gorges du Todgha a été la plus belle route de ma vie. Notre chauffeur Mohamed est un poète au volant.",
      es: "El día en 4x4 cruzando la hammada hasta las gargantas del Todra fue la ruta más espectacular de mi vida. Nuestro chófer Mohamed es un poeta al volante.",
    },
    name: "Daniela Costa",
    origin: "Lisboa, Portugal",
    rating: 5,
    property: null,
    propertyName: null,
    type: "experience",
    audience: "couple",
    date: { en: "February 2025", fr: "Février 2025", es: "Febrero 2025" },
    accent: "#C16542",
  },
  {
    id: 6,
    quote: {
      en: "We were 220 people on a corporate off-site — three properties, three days. The logistics felt invisible. Our team did not.",
      fr: "Nous étions 220 personnes pour un séminaire — trois lieux, trois jours. La logistique était invisible. Notre équipe non.",
      es: "Éramos 220 personas en un off-site corporativo — tres sedes, tres días. La logística fue invisible. Nuestro equipo no.",
    },
    name: "VP People",
    origin: "European SaaS",
    rating: 5,
    property: "kasbah-hotel-xaluca-arfoud",
    propertyName: "Kasbah Hotel Xaluca",
    type: "hotel",
    audience: "corporate",
    date: { en: "November 2024", fr: "Novembre 2024", es: "Noviembre 2024" },
    accent: "#D4A373",
  },
  {
    id: 7,
    quote: {
      en: "I climbed M'Goun from the Ifess refuge in three days, with a Berber guide who knew the names of every plant we passed. I have never felt more held by a mountain.",
      fr: "J'ai gravi le M'Goun depuis le refuge d'Ifess en trois jours, avec un guide berbère qui connaissait le nom de chaque plante. Je ne me suis jamais sentie aussi tenue par une montagne.",
      es: "Subí el M'Goun desde el refugio de Ifess en tres días, con un guía bereber que sabía el nombre de cada planta. Nunca me sentí tan sostenida por una montaña.",
    },
    name: "Ingrid Sørensen",
    origin: "Oslo, Norway",
    rating: 5,
    property: "xaluca-ifess-fess-refuge",
    propertyName: "Xaluca Ifess Refuge",
    type: "refuge",
    audience: "solo",
    date: { en: "July 2025", fr: "Juillet 2025", es: "Julio 2025" },
    accent: "#5C7C4F",
  },
  {
    id: 8,
    quote: {
      en: "I have travelled in 70 countries — Aït Ben Haddou at sunrise from a Xaluca rooftop is in my top three mornings, ever.",
      fr: "J'ai voyagé dans 70 pays — Aït Ben Haddou au lever du soleil depuis une terrasse Xaluca est dans mes trois plus beaux matins, à vie.",
      es: "He viajado a 70 países — Aït Ben Haddou al amanecer desde una azotea Xaluca está entre las tres mejores mañanas de mi vida.",
    },
    name: "Hiroshi Tanaka",
    origin: "Tokyo, Japan",
    rating: 5,
    property: "xaluca-ait-ben-haddou",
    propertyName: "Xaluca Aït Ben Haddou",
    type: "hotel",
    audience: "couple",
    date: { en: "September 2025", fr: "Septembre 2025", es: "Septiembre 2025" },
    accent: "#C16542",
  },
  {
    id: 9,
    quote: {
      en: "We came for the spa and stayed for the bread. I do not know what they do at the wood oven of Sidi Ali, but I want it in my kitchen forever.",
      fr: "Nous sommes venus pour le spa et restés pour le pain. Je ne sais pas ce qu'ils font au four à bois de Sidi Ali, mais je le veux dans ma cuisine à jamais.",
      es: "Vinimos por el spa y nos quedamos por el pan. No sé qué hacen en el horno de leña de Sidi Ali, pero lo quiero en mi cocina para siempre.",
    },
    name: "Karen & Tom",
    origin: "Toronto, Canada",
    rating: 5,
    property: "hotel-xaluca-spa-aguelmame-sidi-ali",
    propertyName: "Hotel Xaluca Sidi Ali",
    type: "hotel",
    audience: "couple",
    date: { en: "August 2025", fr: "Août 2025", es: "Agosto 2025" },
    accent: "#D4A373",
  },
  {
    id: 10,
    quote: {
      en: "A camel caravan into Erg Chebbi at sunset, a feast around the fire, my daughter falling asleep on a Berber rug. Twenty-four hours that the four of us will replay forever.",
      fr: "Une caravane à dos de chameau dans l'Erg Chebbi au coucher du soleil, un festin autour du feu, ma fille endormie sur un tapis berbère. Vingt-quatre heures que nous rejouerons toute notre vie.",
      es: "Una caravana en camello en Erg Chebbi al atardecer, un festín alrededor del fuego, mi hija dormida en una alfombra bereber. Veinticuatro horas que repetiremos toda la vida.",
    },
    name: "Familia López",
    origin: "Barcelona, España",
    rating: 5,
    property: "auberge-la-belle-etoile",
    propertyName: "Auberge La Belle Étoile",
    type: "bivouac",
    audience: "family",
    date: { en: "Easter 2025", fr: "Pâques 2025", es: "Pascua 2025" },
    accent: "#2C2621",
  },
  {
    id: 11,
    quote: {
      en: "The Concierge replied to my WhatsApp at 11pm to confirm a private trout dinner at the lake. That is not customer service. That is hospitality.",
      fr: "La conciergerie m'a répondu sur WhatsApp à 23 h pour confirmer un dîner privé à la truite au bord du lac. Ce n'est pas du service client. C'est de l'hospitalité.",
      es: "La conserjería me respondió por WhatsApp a las 23 h para confirmar una cena privada de trucha en el lago. Eso no es servicio al cliente. Eso es hospitalidad.",
    },
    name: "Olivier R.",
    origin: "Geneva, Switzerland",
    rating: 5,
    property: "hotel-xaluca-spa-aguelmame-sidi-ali",
    propertyName: "Hotel Xaluca Sidi Ali",
    type: "hotel",
    audience: "solo",
    date: { en: "January 2026", fr: "Janvier 2026", es: "Enero 2026" },
    accent: "#5C7C4F",
  },
  {
    id: 12,
    quote: {
      en: "BMW chose Xaluca for our 350-pax launch. They built a stage on a hammada plateau in 36 hours. Zero drama, full magic.",
      fr: "BMW a choisi Xaluca pour notre lancement de 350 personnes. Une scène montée sur un plateau de hamada en 36 heures. Zéro drame, pure magie.",
      es: "BMW eligió a Xaluca para nuestro lanzamiento de 350 personas. Un escenario montado sobre una meseta de hamada en 36 horas. Cero dramas, pura magia.",
    },
    name: "Senior Event Producer",
    origin: "Düsseldorf, Germany",
    rating: 5,
    property: null,
    propertyName: null,
    type: "events",
    audience: "corporate",
    date: { en: "October 2024", fr: "Octobre 2024", es: "Octubre 2024" },
    accent: "#C16542",
  },
  {
    id: 13,
    quote: {
      en: "Eight friends, ten days, four kasbahs. The concierge composed the route around our slowest walker and our loudest laugher.",
      fr: "Huit amis, dix jours, quatre kasbahs. La conciergerie a composé la route autour de notre marcheuse la plus lente et de notre rieur le plus bruyant.",
      es: "Ocho amigos, diez días, cuatro kasbahs. La conserjería compuso la ruta en torno a nuestra caminante más lenta y a nuestro reidor más sonoro.",
    },
    name: "Friends of '02",
    origin: "Amsterdam, NL",
    rating: 5,
    property: null,
    propertyName: null,
    type: "trip",
    audience: "group",
    date: { en: "June 2025", fr: "Juin 2025", es: "Junio 2025" },
    accent: "#D4A373",
  },
  {
    id: 14,
    quote: {
      en: "I asked Lluís for a quiet honeymoon. He gave us a private bivouac, a Berber musician at sunset, and a goodbye breakfast that we still talk about.",
      fr: "J'ai demandé à Lluís une lune de miel calme. Il nous a offert un bivouac privé, un musicien berbère au coucher du soleil et un petit-déjeuner d'adieu dont on parle encore.",
      es: "Le pedí a Lluís una luna de miel tranquila. Nos regaló un bivac privado, un músico bereber al atardecer y un desayuno de despedida del que aún hablamos.",
    },
    name: "Maya & Aman",
    origin: "Mumbai, India",
    rating: 5,
    property: "merdani-bivouac",
    propertyName: "Merdani Bivouac",
    type: "bivouac",
    audience: "honeymoon",
    date: { en: "December 2024", fr: "Décembre 2024", es: "Diciembre 2024" },
    accent: "#2C2621",
  },
  {
    id: 15,
    quote: {
      en: "A solo trip I almost cancelled. Within an hour, the team had me on a 4x4 with a guide. By night two I was singing around a fire with strangers who weren't strangers anymore.",
      fr: "Un voyage solo que j'avais failli annuler. En une heure, l'équipe m'avait mis dans un 4x4 avec un guide. La deuxième nuit, je chantais autour d'un feu avec des inconnus qui ne l'étaient plus.",
      es: "Un viaje en solitario que casi cancelo. En una hora, el equipo me había puesto en un 4x4 con un guía. La segunda noche, cantaba alrededor del fuego con desconocidos que ya no lo eran.",
    },
    name: "Liam O'Connor",
    origin: "Dublin, Ireland",
    rating: 5,
    property: "kamkamia-luxury-desert-camp",
    propertyName: "Kamkamia Desert Camp",
    type: "bivouac",
    audience: "solo",
    date: { en: "September 2024", fr: "Septembre 2024", es: "Septiembre 2024" },
    accent: "#5C7C4F",
  },
  {
    id: 16,
    quote: {
      en: "Our Garmin Titan Desert finish-line camp — 1 000 cyclists, sand storm at dusk. They served hot harira to every one of them. I have seen many race operations. None like this.",
      fr: "Notre arrivée de la Garmin Titan Desert — 1 000 cyclistes, tempête de sable au crépuscule. Ils ont servi de la harira chaude à chacun d'eux. J'ai vu beaucoup d'opérations de course. Aucune comme celle-ci.",
      es: "Nuestra meta de la Garmin Titan Desert — 1 000 ciclistas, tormenta de arena al atardecer. Sirvieron harira caliente a cada uno. He visto muchas operaciones de carrera. Ninguna así.",
    },
    name: "Race Director",
    origin: "Garmin Titan Desert",
    rating: 5,
    property: null,
    propertyName: null,
    type: "events",
    audience: "corporate",
    date: { en: "April 2025", fr: "Avril 2025", es: "Abril 2025" },
    accent: "#C16542",
  },
];

// 3 long-form stories
const STORIES = [
  {
    id: "story-1",
    title: {
      en: "“We came for a holiday. We left with a Moroccan brother.”",
      fr: "« Nous sommes venus en vacances. Nous sommes repartis avec un frère marocain. »",
      es: "«Vinimos de vacaciones. Nos fuimos con un hermano marroquí.»",
    },
    excerpt: {
      en: "How a single mint tea in the courtyard of Kasbah Hotel Xaluca turned into a ten-year friendship between two families, and an annual return trip every Easter.",
      fr: "Comment un seul thé à la menthe dans le patio de la Kasbah Hotel Xaluca s'est transformé en une amitié de dix ans entre deux familles, et un retour annuel chaque Pâques.",
      es: "Cómo un único té a la menta en el patio de la Kasbah Hotel Xaluca se convirtió en una amistad de diez años entre dos familias y un retorno anual cada Pascua.",
    },
    name: "Caroline & Pierre",
    origin: "Lyon, France",
    image: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg",
    tag: { en: "Returning guests", fr: "Hôtes fidèles", es: "Huéspedes fieles" },
    years: "2014 → 2024",
  },
  {
    id: "story-2",
    title: {
      en: "“He proposed at 4:51 am. The sun came up two minutes later.”",
      fr: "« Il m'a demandée en mariage à 4 h 51. Le soleil s'est levé deux minutes plus tard. »",
      es: "«Me pidió matrimonio a las 4:51. El sol salió dos minutos después.»",
    },
    excerpt: {
      en: "A private dune climb arranged in 36 hours by the Xaluca concierge — and the engagement morning that made it into every family album they will ever build.",
      fr: "Une ascension de dune privée organisée en 36 heures par la conciergerie Xaluca — et la matinée de demande en mariage qui figurera dans tous leurs albums de famille à venir.",
      es: "Una subida privada a una duna organizada en 36 horas por la conserjería Xaluca — y la mañana de la propuesta que entrará en todos los álbumes familiares por venir.",
    },
    name: "Naomi & David",
    origin: "Brooklyn, NY",
    image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80",
    tag: { en: "Engagement", fr: "Demande en mariage", es: "Pedida de mano" },
    years: "October 2024",
  },
  {
    id: "story-3",
    title: {
      en: "“My father always wanted to see the Sahara. We carried him on his last birthday.”",
      fr: "« Mon père voulait toujours voir le Sahara. Nous l'y avons porté pour son dernier anniversaire. »",
      es: "«Mi padre siempre quiso ver el Sáhara. Le llevamos en su último cumpleaños.»",
    },
    excerpt: {
      en: "An adapted bivouac, a private nurse alongside the kitchen team, and a sunset that he said was “worth every kilometre.” Three months later, he was gone.",
      fr: "Un bivouac adapté, une infirmière privée aux côtés de la cuisine, et un coucher de soleil qui « valait chaque kilomètre », selon lui. Trois mois plus tard, il s'en allait.",
      es: "Un bivac adaptado, una enfermera privada junto al equipo de cocina y un atardecer que, según él, «valía cada kilómetro». Tres meses después, se fue.",
    },
    name: "Anne-Marie L.",
    origin: "Brussels, Belgium",
    image: "https://images.unsplash.com/photo-1611601679499-c4ec1bdc9be8?auto=format&fit=crop&w=1600&q=80",
    tag: { en: "A family farewell", fr: "Un adieu familial", es: "Una despedida familiar" },
    years: "March 2024",
  },
];

const VIDEOS = [
  {
    id: "vid-1",
    title: {
      en: "Caroline & Pierre — ten years of Easter at Xaluca",
      fr: "Caroline & Pierre — dix ans de Pâques chez Xaluca",
      es: "Caroline y Pierre — diez años de Pascuas en Xaluca",
    },
    duration: "2:14",
    poster: "https://images.unsplash.com/photo-1539020140153-e479b8c5dfd2?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "vid-2",
    title: {
      en: "Sofia & James — a wedding in the dunes",
      fr: "Sofia & James — un mariage dans les dunes",
      es: "Sofía y James — una boda en las dunas",
    },
    duration: "3:42",
    poster: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "vid-3",
    title: {
      en: "Garmin Titan Desert — the finish-line camp",
      fr: "Garmin Titan Desert — l'arrivée au bivouac",
      es: "Garmin Titan Desert — la meta en el campamento",
    },
    duration: "1:58",
    poster: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80",
  },
];

const TYPE_FILTERS = [
  { id: "all", icon: Sparkles, label: { en: "All stays", fr: "Tous les séjours", es: "Todas las estancias" } },
  { id: "hotel", icon: Building, label: { en: "Hotels", fr: "Hôtels", es: "Hoteles" } },
  { id: "bivouac", icon: Tent, label: { en: "Bivouacs", fr: "Bivouacs", es: "Bivacs" } },
  { id: "refuge", icon: Mountain, label: { en: "Refuges", fr: "Refuges", es: "Refugios" } },
  { id: "experience", icon: Sparkles, label: { en: "Experiences", fr: "Expériences", es: "Experiencias" } },
  { id: "trip", icon: ArrowRight, label: { en: "Multi-day trips", fr: "Voyages", es: "Viajes" } },
  { id: "events", icon: Users, label: { en: "Events", fr: "Événements", es: "Eventos" } },
];

const AUDIENCE_FILTERS = [
  { id: "all", label: { en: "Every guest", fr: "Tous les voyageurs", es: "Todos los viajeros" } },
  { id: "couple", label: { en: "Couples", fr: "Couples", es: "Parejas" } },
  { id: "family", label: { en: "Families", fr: "Familles", es: "Familias" } },
  { id: "solo", label: { en: "Solo travellers", fr: "Voyageurs solo", es: "Viajeros solos" } },
  { id: "group", label: { en: "Friends & groups", fr: "Amis & groupes", es: "Amigos y grupos" } },
  { id: "corporate", label: { en: "Corporate & teams", fr: "Entreprises & équipes", es: "Empresas y equipos" } },
  { id: "wedding", label: { en: "Weddings", fr: "Mariages", es: "Bodas" } },
  { id: "honeymoon", label: { en: "Honeymoons", fr: "Lunes de miel", es: "Lunas de miel" } },
];

const TestimonialCard = ({ t, lang, featured }) => (
  <article
    className={`relative bg-white border border-[#E8DFD2] rounded-3xl p-7 md:p-8 flex flex-col shadow-[0_2px_18px_-10px_rgba(44,38,33,0.15)] break-inside-avoid mb-6 ${featured ? "lg:p-10" : ""}`}
    style={{ borderTopColor: t.accent, borderTopWidth: "3px" }}
    data-testid={`testimonial-${t.id}`}
    data-cms-scope={`testimonial.${t.id}`}
  >
    {t.image && (
      <div className="aspect-[16/9] -mx-7 md:-mx-8 -mt-7 md:-mt-8 mb-6 overflow-hidden rounded-t-3xl">
        <img src={t.image} alt={pick(t.quote, lang).slice(0, 60)} className="w-full h-full object-cover" loading="lazy" decoding="async" data-cms-role="avatar"/>
      </div>
    )}
    <div className="flex items-center justify-between mb-4">
      <div className="flex gap-0.5" aria-label={`${t.rating} out of 5`}>
        {Array.from({ length: t.rating }).map((_, i) => (
          <Star key={`star-${t.id || t.author || "rev"}-${i}`} className="w-3.5 h-3.5 fill-[#D4A373] text-[#D4A373]" />
        ))}
      </div>
      {t.propertyName && (
        <span className="text-[9px] tracking-[0.25em] uppercase text-[#5C5248] truncate max-w-[60%]">
          {t.propertyName}
        </span>
      )}
    </div>
    <Quote className="w-5 h-5 text-[#C16542]" strokeWidth={1.5} />
    <p className={`mt-3 font-serif-x text-[#2C2621] leading-snug ${featured ? "text-2xl md:text-3xl" : "text-lg md:text-xl"}`}>
      {pick(t.quote, lang)}
    </p>
    <div className="mt-6 pt-5 border-t border-[#E8DFD2] flex items-end justify-between gap-3">
      <div>
        <p className="font-serif-x text-base text-[#2C2621]">{t.name}</p>
        <p className="text-[11px] tracking-[0.15em] uppercase text-[#5C5248] mt-0.5">{t.origin}</p>
      </div>
      <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] text-right shrink-0">
        {pick(t.date, lang)}
      </p>
    </div>
  </article>
);

const WallOfLovePage = () => {
  const { lang } = useLanguage();
  const [typeFilter, setTypeFilter] = useState("all");
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [activeVideo, setActiveVideo] = useState(null);

  const filtered = useMemo(() => {
    return TESTIMONIALS.filter((t) => {
      if (typeFilter !== "all" && t.type !== typeFilter) return false;
      if (audienceFilter !== "all" && t.audience !== audienceFilter) return false;
      return true;
    });
  }, [typeFilter, audienceFilter]);

  return (
    <div data-testid="page-wall-of-love" className="bg-[#FDFBF7]">
      {/* HERO — multi-slide (video first, then guest stories photography) */}
      <section data-testid="wol-hero">
        <PageHero
          images={[
            HERO,
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/4p3qhcbe_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-3.jpg",
            "https://images.unsplash.com/photo-1473580044384-7ba9967e16a0?auto=format&fit=crop&w=2400&q=80",
          ]}
          alt="Wall of Love · Grup Xaluca"
          scope="page.wall-of-love"
          overline={pick({ en: "Wall of love", fr: "Mur d'amour", es: "Muro del cariño" }, lang)}
          title={pick({
            en: "The voices of those who came, slept, ate, and stayed.",
            fr: "La voix de celles et ceux qui sont venus, ont dormi, mangé et sont restés.",
            es: "Las voces de quienes vinieron, durmieron, comieron y se quedaron.",
          }, lang)}
          sub={pick({
            en: "Real letters, real reviews, real stories — from honeymoons in the dunes to corporate off-sites at altitude. The south of Morocco, told by its guests.",
            fr: "Vraies lettres, vrais avis, vraies histoires — des lunes de miel dans les dunes aux séminaires en altitude. Le sud du Maroc, raconté par ses hôtes.",
            es: "Cartas reales, reseñas reales, historias reales — de lunas de miel en las dunas a off-sites corporativos en altura. El sur de Marruecos, contado por sus huéspedes.",
          }, lang)}
        />
      </section>

      <PageStickyNav
        items={[
          { id: "wol-stats", label: { en: "Numbers", fr: "Chiffres", es: "Cifras" } },
          { id: "wol-grid", label: { en: "Reviews", fr: "Avis", es: "Reseñas" } },
          { id: "wol-stories", label: { en: "Stories", fr: "Histoires", es: "Historias" } },
          { id: "wol-videos", label: { en: "Videos", fr: "Vidéos", es: "Vídeos" } },
          { id: "wol-cta", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />

      {/* STATS STRIP */}
      <section id="wol-stats" className="py-12 md:py-16 border-b border-[#2C2621]/10 scroll-mt-32" data-testid="wol-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left">
          {[
            { v: "4.9 / 5", l: { en: "Average guest rating", fr: "Note moyenne des hôtes", es: "Calificación media" } },
            { v: "100k+", l: { en: "Guests welcomed yearly", fr: "Voyageurs accueillis par an", es: "Huéspedes al año" } },
            { v: "40+", l: { en: "Countries of origin", fr: "Pays d'origine", es: "Países de origen" } },
            { v: "27 yrs", l: { en: "Of letters in our drawer", fr: "De lettres dans nos tiroirs", es: "De cartas en nuestros cajones" } },
          ].map((s) => (
            <div key={`wol-stat-${s.v}`} data-testid={`wol-stat-${s.v}`}>
              <p className="font-serif-x text-4xl md:text-5xl text-[#C16542] leading-none">{s.v}</p>
              <p className="mt-3 text-[11px] tracking-[0.25em] uppercase text-[#5C5248]">{pick(s.l, lang)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FILTERS + GRID */}
      <section id="wol-grid" className="py-20 md:py-24 scroll-mt-32" data-testid="wol-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end gap-6 mb-10">
            <div className="lg:flex-1">
              <p className="overline">
                {pick({ en: "Read the letters", fr: "Lire les lettres", es: "Leer las cartas" }, lang)}
              </p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
                {pick({
                  en: "The wall, curated by you.",
                  fr: "Le mur, filtré par vous.",
                  es: "El muro, filtrado por usted.",
                }, lang)}
              </h2>
            </div>
            <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#5C5248]" data-testid="wol-filter-count">
              <Filter className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
              {filtered.length} / {TESTIMONIALS.length} {pick({ en: "voices", fr: "voix", es: "voces" }, lang)}
            </div>
          </div>

          {/* Type filters */}
          <div className="flex flex-wrap gap-2 mb-3" data-testid="wol-type-filters">
            {TYPE_FILTERS.map((f) => {
              const Icon = f.icon;
              const on = typeFilter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setTypeFilter(f.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                    on
                      ? "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621]"
                      : "bg-white text-[#5C5248] border-[#E8DFD2] hover:border-[#C16542]"
                  }`}
                  data-testid={`wol-type-${f.id}`}
                  aria-pressed={on}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
                  {pick(f.label, lang)}
                </button>
              );
            })}
          </div>

          {/* Audience filters */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="wol-audience-filters">
            {AUDIENCE_FILTERS.map((f) => {
              const on = audienceFilter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setAudienceFilter(f.id)}
                  className={`px-3.5 py-1.5 rounded-full text-[10px] tracking-[0.2em] uppercase border transition-colors ${
                    on
                      ? "bg-[#C16542] text-[#FDFBF7] border-[#C16542]"
                      : "bg-[#FBF6EE] text-[#5C5248] border-[#E8DFD2] hover:border-[#C16542]"
                  }`}
                  data-testid={`wol-audience-${f.id}`}
                  aria-pressed={on}
                >
                  {pick(f.label, lang)}
                </button>
              );
            })}
          </div>

          {/* Masonry grid via CSS columns — keeps the varied card heights organic */}
          {filtered.length > 0 ? (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
              {filtered.map((t, idx) => (
                <TestimonialCard
                  key={t.id}
                  t={t}
                  lang={lang}
                  featured={idx === 0 && filtered.length > 4}
                />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center" data-testid="wol-empty-state">
              <p className="font-serif-x text-3xl text-[#2C2621]">
                {pick({
                  en: "No letter matches these filters — yet.",
                  fr: "Aucune lettre ne correspond à ces filtres — pour l'instant.",
                  es: "Ninguna carta coincide con estos filtros — todavía.",
                }, lang)}
              </p>
              <button
                onClick={() => { setTypeFilter("all"); setAudienceFilter("all"); }}
                className="mt-6 text-[11px] tracking-[0.3em] uppercase text-[#C16542] border-b border-[#C16542]/40 pb-1 hover:border-[#C16542] transition-colors"
                data-testid="wol-clear-filters"
              >
                {pick({ en: "Clear filters", fr: "Réinitialiser", es: "Restablecer" }, lang)}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CLIENT STORIES */}
      <section id="wol-stories" className="py-24 md:py-32 bg-[#F2EBE1]/50 scroll-mt-32" data-testid="wol-stories">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
            <div className="md:col-span-7">
              <p className="overline">
                {pick({ en: "Client stories", fr: "Histoires de nos hôtes", es: "Historias de nuestros huéspedes" }, lang)}
              </p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
                {pick({
                  en: "Three memories the south kept.",
                  fr: "Trois souvenirs que le sud a gardés.",
                  es: "Tres recuerdos que el sur conservó.",
                }, lang)}
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {STORIES.map((s) => (
              <article
                key={s.id}
                className="bg-white rounded-3xl border border-[#E8DFD2] overflow-hidden flex flex-col shadow-[0_2px_18px_-10px_rgba(44,38,33,0.15)]"
                data-testid={`wol-story-${s.id}`}
                data-cms-scope={`wol-story.${s.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={s.image} alt={pick(s.title, lang)} className="w-full h-full object-cover" loading="lazy" decoding="async" data-cms-role="card.image"/>
                </div>
                <div className="p-7 md:p-8 flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-[#C16542] border border-[#C16542]/40 px-2.5 py-1 rounded-full">
                      {pick(s.tag, lang)}
                    </span>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5248]">{s.years}</span>
                  </div>
                  <h3 className="font-serif-x text-2xl md:text-[26px] mt-5 leading-tight text-[#2C2621]">
                    {pick(s.title, lang)}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-[#5C5248] flex-1">
                    {pick(s.excerpt, lang)}
                  </p>
                  <div className="mt-6 pt-5 border-t border-[#E8DFD2] flex items-end justify-between gap-3">
                    <div>
                      <p className="font-serif-x text-base text-[#2C2621]">{s.name}</p>
                      <p className="text-[11px] tracking-[0.15em] uppercase text-[#5C5248] mt-0.5">{s.origin}</p>
                    </div>
                    <MapPin className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO TESTIMONIALS */}
      <section id="wol-videos" className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32" data-testid="wol-videos">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
            <div className="md:col-span-7">
              <p className="overline !text-[#D4A373]">
                {pick({ en: "Video testimonials", fr: "Témoignages vidéo", es: "Testimonios en vídeo" }, lang)}
              </p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {pick({
                  en: "Hear the south in their own voices.",
                  fr: "Entendre le sud dans leurs propres mots.",
                  es: "Escuchar el sur en sus propias voces.",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base text-[#FDFBF7]/75 leading-relaxed">
                {pick({
                  en: "Short films our guests agreed to leave behind — sat on a kasbah terrace, beside a fire, on the morning of a wedding.",
                  fr: "Courts films que nos hôtes ont accepté de nous laisser — assis sur une terrasse, près d'un feu, le matin d'un mariage.",
                  es: "Cortos que nuestros huéspedes aceptaron dejarnos — sentados en una terraza, junto a una hoguera, la mañana de una boda.",
                }, lang)}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {VIDEOS.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setActiveVideo(v)}
                className="group relative overflow-hidden aspect-[4/5] rounded-3xl text-left"
                data-testid={`wol-video-${v.id}`}
                data-cms-scope={`wol-video.${v.id}`}
              >
                <img src={v.poster} alt={pick(v.title, lang)} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" data-cms-role="poster"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/95 via-[#1A1513]/35 to-transparent" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#FDFBF7]/95 text-[#C16542] flex items-center justify-center group-hover:bg-[#C16542] group-hover:text-[#FDFBF7] transition-colors shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
                    <Play className="w-6 h-6 md:w-7 md:h-7 ml-1" strokeWidth={1.5} fill="currentColor" />
                  </span>
                </span>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4A373]">{v.duration}</p>
                  <h3 className="font-serif-x text-xl md:text-2xl mt-2 leading-tight text-[#FDFBF7]">
                    {pick(v.title, lang)}
                  </h3>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section id="wol-cta" className="py-24 md:py-32 scroll-mt-32" data-testid="wol-cta">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Heart className="w-7 h-7 mx-auto text-[#C16542] fill-[#C16542]/15" strokeWidth={1.5} />
            <h2 className="font-serif-x mt-6 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
              {pick({
                en: "Write your own line on the wall.",
                fr: "Écrivez votre ligne sur le mur.",
                es: "Escriba su línea en el muro.",
              }, lang)}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                title: { en: "Request a stay", fr: "Demander un séjour", es: "Solicitar una estancia" },
                desc: {
                  en: "One hotel or many — our concierge composes the route around you.",
                  fr: "Un hôtel ou plusieurs — la conciergerie compose la route autour de vous.",
                  es: "Un hotel o varios — la conserjería compone la ruta a su medida.",
                },
                to: "/booking",
                testid: "wol-cta-booking",
                cta: { en: "Send a request", fr: "Envoyer une demande", es: "Enviar una solicitud" },
                dark: true,
              },
              {
                title: { en: "Contact the team", fr: "Contacter l'équipe", es: "Contactar el equipo" },
                desc: {
                  en: "WhatsApp, email or phone — answers in FR · EN · ES, within 24 hours.",
                  fr: "WhatsApp, email ou téléphone — réponses en FR · EN · ES sous 24 h.",
                  es: "WhatsApp, email o teléfono — respuestas en FR · EN · ES en 24 h.",
                },
                to: "/contact",
                testid: "wol-cta-contact",
                cta: { en: "Get in touch", fr: "Nous écrire", es: "Escribirnos" },
                dark: false,
              },
              {
                title: { en: "Explore the properties", fr: "Découvrir les adresses", es: "Descubrir las direcciones" },
                desc: {
                  en: "From a kasbah in Arfoud to a refuge in the Atlas — find the door that fits.",
                  fr: "D'une kasbah à Arfoud à un refuge dans l'Atlas — trouvez la porte qui vous va.",
                  es: "De una kasbah en Arfoud a un refugio en el Atlas — encuentre la puerta que le encaja.",
                },
                to: "/hotels",
                testid: "wol-cta-hotels",
                cta: { en: "Discover the hotels", fr: "Découvrir les hôtels", es: "Descubrir los hoteles" },
                dark: false,
              },
            ].map((c) => (
              <Link
                key={c.testid}
                to={c.to}
                className={`group p-7 md:p-8 rounded-3xl border transition-all flex flex-col ${
                  c.dark
                    ? "bg-[#2C2621] border-[#2C2621] text-[#FDFBF7] hover:bg-[#C16542] hover:border-[#C16542]"
                    : "bg-white border-[#E8DFD2] text-[#2C2621] hover:border-[#C16542]"
                }`}
                data-testid={c.testid}
              >
                <h3 className={`font-serif-x text-2xl md:text-3xl leading-tight ${c.dark ? "text-[#FDFBF7]" : "text-[#2C2621]"}`}>
                  {pick(c.title, lang)}
                </h3>
                <p className={`mt-4 text-[15px] leading-relaxed flex-1 ${c.dark ? "text-[#FDFBF7]/75" : "text-[#5C5248]"}`}>
                  {pick(c.desc, lang)}
                </p>
                <span className={`mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase border-b pb-1 w-fit transition-colors ${
                  c.dark ? "text-[#D4A373] border-[#D4A373]/60" : "text-[#C16542] border-[#C16542]/40 group-hover:border-[#C16542]"
                }`}>
                  {pick(c.cta, lang)}
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO MODAL */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] bg-[#1A1513]/95 flex items-center justify-center px-4 md:px-12"
          onClick={() => setActiveVideo(null)}
          data-testid="wol-video-modal"
        >
          <button
            onClick={() => setActiveVideo(null)}
            className="absolute top-6 right-6 text-[#FDFBF7]/80 hover:text-[#FDFBF7] p-2"
            data-testid="wol-video-close"
            aria-label={pick({ en: "Close", fr: "Fermer", es: "Cerrar" }, lang)}
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Match the card `<img>`'s scope+role so `applyOverrides`
                rewrites this modal image with the same DB override —
                otherwise the card shows the admin's custom upload while
                the modal shows the hardcoded fallback. */}
            <div className="aspect-video bg-[#1A1513] rounded-2xl overflow-hidden relative" data-cms-scope={`wol-video.${activeVideo.id}`}>
              <img src={activeVideo.poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" loading="lazy" decoding="async" data-cms-role="poster"/>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <Play className="w-12 h-12 text-[#D4A373]" strokeWidth={1.2} fill="currentColor" />
                <p className="font-serif-x text-3xl md:text-4xl mt-6 text-[#FDFBF7] max-w-2xl">
                  {pick(activeVideo.title, lang)}
                </p>
                <p className="mt-4 text-[11px] tracking-[0.3em] uppercase text-[#D4A373]">
                  {pick({
                    en: "Video coming soon · in production with our guests",
                    fr: "Vidéo bientôt disponible · en production avec nos hôtes",
                    es: "Vídeo próximamente · en producción con nuestros huéspedes",
                  }, lang)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WallOfLovePage;
